import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { blog } from "../lib/api";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "DC Guides", label: "DC Guides" },
  { key: "Nightlife", label: "Nightlife" },
  { key: "VIP", label: "VIP" },
  { key: "Events", label: "Events" },
  { key: "Lifestyle", label: "Lifestyle" },
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    blog.list(filter === "all" ? {} : { category: filter }).then((d) => {
      setPosts(d); setLoading(false);
    }).catch(() => setLoading(false));
  }, [filter]);

  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = posts.filter((p) => p.id !== featured?.id);

  // Inject SEO meta on this page
  useEffect(() => {
    document.title = "The Onyx Journal — DC Nightlife Guides & Stories | Onyx Rooftop DC";
    let desc = document.querySelector("meta[name='description']");
    if (!desc) { desc = document.createElement("meta"); desc.name = "description"; document.head.appendChild(desc); }
    desc.content = "Washington DC nightlife guides, rooftop stories, VIP bottle service tips, and Onyx event recaps. The official editorial blog of Onyx Rooftop Lounge in Dupont Circle.";
  }, []);

  return (
    <main data-testid="blog-page" className="bg-white min-h-screen pt-28 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 mb-14">
        <span className="eyebrow">— The Onyx Journal</span>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-4 text-ink leading-[1]">DC nightlife, <span className="text-gold">decoded</span>.</h1>
        <p className="text-muted max-w-2xl mt-5 text-lg">Guides, stories, and insider intel on Washington DC's best rooftop nightlife — straight from the team behind Onyx.</p>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="blog-filter">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`blog-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-5 py-2.5 text-[11px] tracking-luxe uppercase border transition ${
                filter === c.key ? "border-ink bg-ink text-white" : "border-black/15 text-black/65 hover:border-ink hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted italic">Loading articles…</p>
        ) : posts.length === 0 ? (
          <p className="text-muted italic">No articles in this category yet.</p>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-featured-${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border border-black/10 hover:border-ink transition mb-14 bg-white"
              >
                <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto overflow-hidden bg-black">
                  {featured.cover_image_url ? (
                    <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : null}
                </div>
                <div className="lg:col-span-5 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <p className="text-[10px] tracking-luxe uppercase text-gold mb-3">Featured · {featured.category} · {featured.reading_minutes} min read</p>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.05] mb-5">{featured.title}</h2>
                  <p className="text-muted leading-relaxed mb-7">{featured.excerpt}</p>
                  <span className="text-[11px] tracking-luxe uppercase text-ink group-hover:text-gold transition">Read article →</span>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-white border border-black/10 hover:border-ink transition-all duration-300 hover:-translate-y-1 flex flex-col"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-black">
                    {p.cover_image_url ? (
                      <img src={p.cover_image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : null}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[10px] tracking-luxe uppercase text-gold mb-2">{p.category} · {p.reading_minutes} min</p>
                    <h3 className="font-display text-xl md:text-2xl text-ink leading-tight mb-3 line-clamp-2">{p.title}</h3>
                    <p className="text-sm text-muted leading-relaxed line-clamp-3 mb-5">{p.excerpt}</p>
                    <span className="mt-auto text-[10px] tracking-luxe uppercase text-ink group-hover:text-gold transition">Read →</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
